import { defineMessages } from 'react-intl';

const messages = defineMessages({
  defaultTitle: {
    id: 'Embla Carousel',
    defaultMessage: 'Embla Carousel',
  },
});

const emblaCarouselSchema = ({ formData } = {}) => ({
  title: messages.defaultTitle,
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: [
        'title',
        'slides',
        'slidesToShow',
        'minSlidesOnMobile',
        'displayMode',
        'autoplay',
        'autoplayDelay',
        'alignment',
        'isFullWidth',
        'outerClassName',
        'loop',
        'hideArrows',
        'hideDots',
        'hideButtons',
        'clickableSlides',
        'hideDescription',
        'showEffectiveDate',
        'equalHeight',
        'backgroundColor',
        'textTone',
      ],
    },
    {
      id: 'marquee',
      title: 'Logo marquee options',
      fields: [
        'marqueeHelp',
        'marqueeSpeed',
        'logoHeight',
        'logoGap',
        'logoPadding',
        'pauseOnHover',
      ],
    },
  ],
  properties: {
    outerClassName: {
      title: 'Custom container class',
      description: 'Optional extra class applied on the outer .embla wrapper',
      type: 'string',
    },
    title: {
      title: 'Carousel title (h2)',
      description: 'Optional title shown above the carousel',
      type: 'string',
    },
    slides: {
      title: 'Slides',
      widget: 'object_list',
      schema: {
        title: 'Slide',
        titleField: 'heading',
        fieldsets: [
          {
            id: 'default',
            title: 'Default',
            fields: ['heading', 'content', 'image', 'link', 'buttonText'],
          },
        ],
        properties: {
          heading: {
            title: 'Heading',
            type: 'string',
          },
          content: {
            title: 'Content',
            type: 'text',
          },
          image: {
            title: 'Image',
            widget: 'object_browser',
            mode: 'image',
            allowExternals: false,
          },
          link: {
            title: 'Link',
            widget: 'object_browser',
            mode: 'link',
            allowExternals: true,
            multi: false,
            default: null,
          },
          buttonText: {
            title: 'Button text',
            type: 'string',
          },
        },
        required: [],
      },
    },
    displayMode: {
      title: 'Display mode',
      type: 'string',
      choices: [
        ['full', 'Full (image + text)'],
        ['image-only', 'Image only'],
        ['image-top', 'Image top (image above content)'],
        ['logo-marquee', 'Logo marquee (scrolling logos)'],
      ],
      default: 'full',
    },
    alignment: {
      title: 'Text alignment',
      widget: 'align',
      actions: ['left', 'center', 'right'],
      default: 'left',
    },
    isFullWidth: {
      title: 'Full width carousel',
      type: 'boolean',
      default: false,
    },
    loop: {
      title: 'Loop infinitely',
      type: 'boolean',
      default: true,
    },
    slidesToShow: {
      title: 'Slides to show (desktop)',
      type: 'number',
      default: 1,
    },
    minSlidesOnMobile: {
      title: 'Minimum slides on mobile',
      description: 'Number of cards visible on screens ≤768px',
      type: 'number',
      default: 1,
    },
    autoplay: {
      title: 'Autoplay',
      type: 'boolean',
    },
    autoplayDelay: {
      title: 'Autoplay delay (ms)',
      type: 'number',
      default: 8000,
    },
    clickableSlides: {
      title: 'Make slides clickable',
      type: 'boolean',
      default: false,
    },
    hideDescription: {
      title: 'Hide description/content',
      type: 'boolean',
      default: false,
    },
    equalHeight: {
      title: 'Make all slides equal height',
      type: 'boolean',
      default: false,
    },
    showEffectiveDate: {
      title: 'Show effective date',
      type: 'boolean',
      default: false,
    },
    hideArrows: {
      title: 'Hide arrows',
      type: 'boolean',
    },
    hideDots: {
      title: 'Hide dots',
      type: 'boolean',
    },
    hideButtons: {
      title: 'Hide buttons',
      type: 'boolean',
      default: false,
    },
    backgroundColor: {
      title: 'Background colour',
      widget: 'select',
      choices: [
        ['#ffffff', 'White'],
        ['var(--gold-tint-10)', 'Gold tint'],
        ['var(--gold-darker)', 'Gold Darker'],
        ['var(--blue-tint-10)', 'Blue tint'],
        ['var(--blue-darker)', 'Blue Darker'],
        ['var(--turquoise-tint-10)', 'Turquoise tint'],
        ['var(--turquoise-darker)', 'Turquoise Darker'],
        ['var(--coral-tint-10)', 'Coral tint'],
        ['var(--coral-darker)', 'Coral Darker'],
        ['var(--off-white)', 'Off white'],
        ['var(--charcoal)', 'Charcoal'],
      ],
      default: '#E4EBEE',
    },
    textTone: {
      title: 'Title & dots colour',
      widget: 'select',
      choices: [
        ['light', 'Light'],
        ['dark', 'Dark'],
      ],
      default: 'dark',
    },

    // Logo marquee specific fields
    marqueeHelp: {
      title: 'Logo marquee help',
      description:
        'For the logo marquee mode, only the image from each slide/listing item is used. Add slides with images (logos). Links are optional and will make logos clickable.',
      type: 'string',
    },
    marqueeSpeed: {
      title: 'Marquee duration (seconds)',
      description: 'Time for one full loop across the screen',
      type: 'number',
      default: 20,
    },
    logoHeight: {
      title: 'Logo height (px)',
      type: 'number',
      default: 48,
    },
    logoGap: {
      title: 'Gap between logos (px)',
      type: 'number',
      default: 32,
    },
    logoPadding: {
      title: 'Logo padding (px)',
      type: 'number',
      default: 8,
    },
    pauseOnHover: {
      title: 'Pause on hover',
      type: 'boolean',
      default: true,
    },
  },
  required: [],
  fieldset_condition: {
    marquee: (formData) => formData?.displayMode === 'logo-marquee',
  },
});

export default emblaCarouselSchema;